import { useMemo } from "react";
import { addDays, compareIsoDate, todayIn, zonedParts } from "../../../shared/dates";
import { daysUntil } from "../../../shared/home";
import { convertTemp } from "../../../shared/weather";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf, useCalendarEvents, useMembers, useTasks } from "../../data/queries";
import { Avatar } from "../../ui/Avatar";
import { Icon, type IconName } from "../../ui/Icon";
import { StatusBadge } from "../../ui/StatusBadge";
import { WeatherIcon } from "../../ui/WeatherIcon";
import { formatShortTime, useNow } from "../../ui/time";
import { eventsOnDay, fmtDate, placeEvent } from "../calendar/calendarModel";
import { useMeals } from "../kitchen/api";
import type { WidgetDefinition, WidgetProps } from "../types";
import { useWeatherSummary } from "../weather/WeatherWidget";
import { WidgetHeader } from "../WidgetHeader";
import { useCountdowns } from "./api";
import "./home.css";
import { remPx } from "../sky/common";

type TomorrowConfig = Record<string, never>;

/**
 * Tomorrow at a glance, for the Night screen: the weather (with what to
 * wear), the first thing on the calendar, dinner, what's due and anything
 * the family is counting down to. Built from data already on the wall.
 */
function Tomorrow({ size }: WidgetProps<TomorrowConfig>) {
  const { timeZone, settings } = useHousehold();
  const now = useNow("minute");
  const today = todayIn(timeZone, now);
  // After midnight "tomorrow" means the day that is starting.
  const tomorrow = zonedParts(now, timeZone).minutes < 4 * 60 ? today : addDays(today, 1);
  const cal = useCalendarEvents(today, addDays(today, 2));
  const meals = useMeals(today, addDays(today, 1));
  const tasks = useTasks();
  const countdowns = useCountdowns();
  const members = useMembers().data ?? [];
  const byId = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const wx = useWeatherSummary();
  const state = dataStateOf(cal, 20 * 60_000);
  const unit = settings.temperatureUnit;

  const events = useMemo(
    () => eventsOnDay((cal.data?.events ?? []).filter((e) => e.status !== "cancelled").map((e) => placeEvent(e, timeZone)), tomorrow),
    [cal.data, timeZone, tomorrow],
  );
  const timed = events.filter((p) => !p.event.allDay);
  const allDay = events.filter((p) => p.event.allDay);
  const first = timed[0];
  const dinner = (meals.data ?? []).find((m) => m.date === tomorrow && m.slot === "dinner");
  const due = (tasks.data ?? []).filter((t) => !t.completedAt && t.dueDate === tomorrow);
  const special = (countdowns.data ?? []).filter((c) => c.date === tomorrow);
  const nextCountdown = (countdowns.data ?? []).filter((c) => compareIsoDate(c.date, tomorrow) > 0).sort((a, b) => a.date.localeCompare(b.date))[0];

  // Tomorrow's daytime forecast and the night before it.
  const daily = wx.q.data?.daily ?? [];
  const day = daily.find((p) => p.isDaytime && zonedParts(new Date(p.startTime), timeZone).date === tomorrow);
  const night = daily.find((p) => !p.isDaytime && zonedParts(new Date(p.startTime), timeZone).date === addDays(tomorrow, -1));
  const highF = day ? convertTemp(day.temperature, day.temperatureUnit, "F") : null;
  const wear: { icon: IconName; text: string }[] = [];
  if (highF !== null) {
    if (highF < 40) wear.push({ icon: "thermo", text: "Winter coat, hat and gloves" });
    else if (highF < 55) wear.push({ icon: "thermo", text: "Jacket weather" });
    else if (highF < 68) wear.push({ icon: "thermo", text: "Bring a layer" });
    else if (highF >= 88) wear.push({ icon: "sun", text: "Hot: water bottles and sunscreen" });
  }
  if ((day?.precipChancePct ?? 0) >= 40) wear.push({ icon: "umbrella", text: /snow/i.test(day!.shortForecast) ? "Snow boots" : "Umbrella or rain jacket" });

  const t = (p: { temperature: number; temperatureUnit: "F" | "C" }) => `${Math.round(convertTemp(p.temperature, p.temperatureUnit, unit))}°`;
  const compact = size.height < remPx() * 16.9;

  return (
    <div className="home-w tomorrow">
      <WidgetHeader title={`Tomorrow · ${fmtDate(tomorrow, { weekday: "long" })}`} meta={fmtDate(tomorrow, { month: "long", day: "numeric" })} />
      {special.length > 0 && (
        <div className="tm-special">
          {special.map((c) => (
            <span key={c.id}>
              {c.emoji} {c.title} is tomorrow!
            </span>
          ))}
        </div>
      )}
      <div className="tm-grid">
        {day && (
          <div className="tm-wx">
            <WeatherIcon kind={day.icon} size={compact ? 44 : 58} />
            <span className="tm-wx-text">
              <strong>
                {t(day)}
                {night && <span className="muted"> / {t(night)}</span>}
              </strong>
              <span>{day.shortForecast}</span>
              {(day.precipChancePct ?? 0) >= 20 && <span className="muted">{day.precipChancePct}% chance of precipitation</span>}
            </span>
          </div>
        )}
        {wear.length > 0 && (
          <ul className="tm-wear">
            {wear.map((w) => (
              <li key={w.text}>
                <Icon name={w.icon} size="1rem" /> {w.text}
              </li>
            ))}
          </ul>
        )}
      </div>
      <ul className="tm-list">
        <li>
          <Icon name="calendar" size="1.1rem" />
          {events.length === 0 ? (
            <span className="muted">Nothing on the calendar</span>
          ) : (
            <span>
              {first ? (
                <>
                  First up <strong>{formatShortTime(new Date(first.startMs), timeZone, settings.clock24h)}</strong> {first.event.displayTitle}
                </>
              ) : (
                <strong>{allDay[0]!.event.displayTitle}</strong>
              )}
              {events.length > 1 && <span className="muted"> · {events.length} events in all</span>}
            </span>
          )}
          {first?.event.people.slice(0, 3).map((id) => byId.get(id) && <Avatar key={id} member={byId.get(id)!} size="1.5rem" />)}
        </li>
        <li>
          <Icon name="utensils" size="1.1rem" />
          {dinner ? (
            <span>
              Dinner: <strong>{dinner.title}</strong>
            </span>
          ) : (
            <span className="muted">No dinner planned yet</span>
          )}
        </li>
        {due.length > 0 && (
          <li>
            <Icon name="tasks" size="1.1rem" />
            <span>
              Due: {due.slice(0, 3).map((x) => x.title).join(", ")}
              {due.length > 3 && ` and ${due.length - 3} more`}
            </span>
          </li>
        )}
        {nextCountdown && !compact && (
          <li>
            <Icon name="hourglass" size="1.1rem" />
            <span>
              {nextCountdown.emoji} {daysUntil(nextCountdown.date, today)} sleeps until {nextCountdown.title}
            </span>
          </li>
        )}
      </ul>
      <StatusBadge state={state} updatedAt={cal.dataUpdatedAt} />
    </div>
  );
}

export const tomorrowWidget: WidgetDefinition<TomorrowConfig> = {
  type: "tomorrow",
  version: 1,
  name: "Tomorrow at a glance",
  description: "Tomorrow’s weather and what to wear, the first thing on the calendar, dinner, what’s due and countdowns. Made for the Night screen.",
  icon: "moon",
  category: "household",
  defaultSize: { w: 6, h: 5 },
  minSize: { w: 3, h: 3 },
  stackHeight: 360,
  defaults: {},
  settings: [],
  Component: Tomorrow,
};
